/**
 * The geometry behind the workflow chain diagram — extracted from the SVG so the
 * layout is a pure function a test can hold still, exactly as
 * src/pages/platform/hierarchy-layout.ts does for the template ladder.
 *
 * WHAT THE PICTURE CLAIMS. A maker-checker workflow is a straight line with one
 * standing threat: the maker starts a case, each step must pass in order, and the
 * case ends approved. Rejection is not a step — it is an EXIT available from
 * every step, which is why the rejected outcome is drawn as a side node with one
 * red edge leaving each step, never as a rung on the chain. Drawing it inline
 * would claim rejection happens "after" some step, and it doesn't; it happens
 * INSTEAD of the rest of the chain.
 *
 * The same geometry serves both renders of the diagram: the designer's live
 * preview (no statuses) and the instance detail (decided steps filled in). The
 * layout is status-blind on purpose — where things sit never depends on how the
 * case is going, so both pictures stay comparable at a glance.
 */

export interface ChainNode {
  kind: 'maker' | 'step' | 'approved' | 'rejected';
  /** 0-based index into the steps for kind 'step'; -1 for the fixed nodes. */
  stepIndex: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ChainEdge {
  /** 'flow' walks the happy path top-down; 'reject' exits a step to the side. */
  kind: 'flow' | 'reject';
  /** Which step a reject edge leaves from; -1 on flow edges from the maker. */
  fromStep: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface ChainLayout {
  width: number;
  height: number;
  nodes: ChainNode[];
  edges: ChainEdge[];
}

/* Geometry constants. Kept here, not in the component, so the tests pin the same
 * numbers the SVG draws with. */
export const CHAIN_NODE_WIDTH = 252;
export const MAKER_HEIGHT = 46;
export const STEP_HEIGHT = 62;
export const OUTCOME_HEIGHT = 46;
export const CHAIN_GAP = 34;
export const CHAIN_MARGIN = 12;
/** Horizontal clearance between the chain column and the rejected exit node. */
export const REJECT_COLUMN_GAP = 48;
export const REJECT_WIDTH = 150;
export const REJECT_HEIGHT = 46;

/**
 * Lays out maker → step... → approved top-down, with the rejected exit as a
 * right-hand node level with the middle of the step run.
 *
 * The function takes a COUNT, not the steps themselves: nothing about the
 * geometry depends on titles or approvers, and keeping the signature to a number
 * makes it impossible for layout to accidentally grow opinions about content.
 *
 * Zero steps still draws maker → approved with no rejected node and no red
 * edges: a chain with no checkpoints approves unconditionally, and the honest
 * picture of that is a straight line nobody can veto.
 */
export function layoutChain(stepCount: number): ChainLayout {
  const count = Math.max(0, Math.floor(stepCount));
  const columnX = CHAIN_MARGIN;

  const nodes: ChainNode[] = [];
  const edges: ChainEdge[] = [];

  const maker: ChainNode = {
    kind: 'maker',
    stepIndex: -1,
    x: columnX,
    y: CHAIN_MARGIN,
    width: CHAIN_NODE_WIDTH,
    height: MAKER_HEIGHT,
  };
  nodes.push(maker);

  let cursorY = maker.y + maker.height + CHAIN_GAP;
  const stepNodes: ChainNode[] = [];
  for (let index = 0; index < count; index += 1) {
    const node: ChainNode = {
      kind: 'step',
      stepIndex: index,
      x: columnX,
      y: cursorY,
      width: CHAIN_NODE_WIDTH,
      height: STEP_HEIGHT,
    };
    stepNodes.push(node);
    nodes.push(node);
    cursorY += STEP_HEIGHT + CHAIN_GAP;
  }

  const approved: ChainNode = {
    kind: 'approved',
    stepIndex: -1,
    x: columnX,
    y: cursorY,
    width: CHAIN_NODE_WIDTH,
    height: OUTCOME_HEIGHT,
  };
  nodes.push(approved);

  // The happy path: centre-bottom of each node to centre-top of the next.
  const column = [maker, ...stepNodes, approved];
  for (let index = 0; index + 1 < column.length; index += 1) {
    const from = column[index];
    const to = column[index + 1];
    edges.push({
      kind: 'flow',
      fromStep: from.kind === 'step' ? from.stepIndex : -1,
      x1: from.x + from.width / 2,
      y1: from.y + from.height,
      x2: to.x + to.width / 2,
      y2: to.y,
    });
  }

  let rejected: ChainNode | null = null;
  if (count > 0) {
    // Level with the vertical middle of the step run, so the eye reads it as
    // "reachable from any of these", which is exactly the claim.
    const runTop = stepNodes[0].y;
    const runBottom = stepNodes[stepNodes.length - 1].y + STEP_HEIGHT;
    rejected = {
      kind: 'rejected',
      stepIndex: -1,
      x: columnX + CHAIN_NODE_WIDTH + REJECT_COLUMN_GAP,
      y: (runTop + runBottom) / 2 - REJECT_HEIGHT / 2,
      width: REJECT_WIDTH,
      height: REJECT_HEIGHT,
    };
    nodes.push(rejected);

    // One red exit per step: right edge of the step to the left edge of the
    // rejected node, each keeping its own y so the fan stays legible.
    for (const node of stepNodes) {
      edges.push({
        kind: 'reject',
        fromStep: node.stepIndex,
        x1: node.x + node.width,
        y1: node.y + node.height / 2,
        x2: rejected.x,
        y2: rejected.y + rejected.height / 2,
      });
    }
  }

  return {
    width:
      rejected !== null
        ? rejected.x + REJECT_WIDTH + CHAIN_MARGIN
        : CHAIN_MARGIN + CHAIN_NODE_WIDTH + CHAIN_MARGIN,
    height: approved.y + OUTCOME_HEIGHT + CHAIN_MARGIN,
    nodes,
    edges,
  };
}

/**
 * Ellipsis for SVG <text>, which has no CSS overflow. The cut is by character
 * budget, not pixels — close enough at the diagram's fixed font sizes, and a
 * pure function the tests can pin, where measuring glyphs would drag a canvas in.
 */
export function truncateLabel(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  return `${text.slice(0, Math.max(0, maxChars - 1)).trimEnd()}…`;
}
